import { useState } from "react";
import AppHeader from "./AppHeader";

// ── Help & Support: fully static. No backend, no network, no API token. ───────
const C = {
  white:"#FFFFFF", bg:"#F8FAFB", border:"#E8EEF2",
  blue:"#51B0E6", blueDark:"#2A8FCA", blueLight:"#EDF6FC",
  body:"#1A2535", muted:"#6B7A8D", green:"#1E8A4C",
};

const SUPPORT_EMAIL = "support@generositywater.com"; // ASSUMPTION: confirm the live inbox

function HIcon({ name, size=20, color=C.blue }) {
  const s=size, ic=color;
  const icons = {
    drop: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 3s6 7 6 11a6 6 0 0 1-12 0c0-4 6-11 6-11Z" stroke={ic} strokeWidth="1.6" strokeLinejoin="round"/></svg>,
    ring: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="7" stroke={ic} strokeWidth="2"/></svg>,
    wrench: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M14 7a4 4 0 0 1-5 5l-5 5 2 2 5-5a4 4 0 0 0 5-5l-2 2-2-2 2-2Z" stroke={ic} strokeWidth="1.5" strokeLinejoin="round"/></svg>,
    shield: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 3l7 3v6c0 4-3 7-7 9-4-2-7-5-7-9V6l7-3Z" stroke={ic} strokeWidth="1.6" strokeLinejoin="round"/></svg>,
    mail: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="14" rx="2" stroke={ic} strokeWidth="1.6"/><path d="M4 7l8 6 8-6" stroke={ic} strokeWidth="1.6" strokeLinejoin="round"/></svg>,
  };
  return <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{icons[name]||icons.drop}</span>;
}

function Section({ icon, title, children }) {
  return (
    <div style={{background:C.white,borderRadius:18,border:`1.5px solid ${C.border}`,padding:"4px 16px"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 0 6px"}}>
        <div style={{width:34,height:34,borderRadius:10,flexShrink:0,background:C.blueLight,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <HIcon name={icon} size={18} color={C.blueDark}/>
        </div>
        <div style={{fontSize:15,fontWeight:800,color:C.body}}>{title}</div>
      </div>
      {children}
    </div>
  );
}

function FAQ({ id, q, openId, setOpenId, children }) {
  const open = openId === id;
  return (
    <div style={{borderTop:`1px solid ${C.border}`}}>
      <button onClick={()=>setOpenId(open?null:id)} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,padding:"14px 0",background:"none",border:"none",cursor:"pointer",textAlign:"left",fontFamily:"inherit"}}>
        <span style={{fontSize:14,fontWeight:700,color:C.body}}>{q}</span>
        <span style={{fontSize:20,lineHeight:1,color:C.muted,flexShrink:0,transform:open?"rotate(45deg)":"none",transition:"transform 0.2s"}}>+</span>
      </button>
      {open && <div style={{fontSize:13,color:C.muted,lineHeight:1.65,padding:"0 0 16px"}}>{children}</div>}
    </div>
  );
}

export default function HelpScreen({ onClose }) {
  const [openId, setOpenId] = useState(null);

  return (
    <div data-testid="help-screen" style={{fontFamily:"Nunito Sans,-apple-system,sans-serif",background:C.white,minHeight:"100%",color:C.body,display:"flex",flexDirection:"column",maxWidth:480,margin:"0 auto",WebkitFontSmoothing:"antialiased"}}>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Nunito+Sans:wght@400;600;700&display=swap" rel="stylesheet"/>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}} *{box-sizing:border-box;-webkit-tap-highlight-color:transparent}`}</style>

      <AppHeader
        variant="modal"
        title="Help & Support"
        subtitle="Answers and how-tos"
        onClose={onClose}
      />

      <div style={{flex:1,overflowY:"auto",padding:"20px",display:"flex",flexDirection:"column",gap:16,paddingBottom:40,animation:"fadeIn 0.3s ease"}}>

        <Section icon="drop" title="Your hydration">
          <FAQ id="nhs" q="What is my Net Hydration Score?" openId={openId} setOpenId={setOpenId}>
            It is a daily 0 to 100 read on how well hydrated you are, built from what you drink, what your body needs, and, when a wearable is connected, signals like sleep and readiness. It is an estimate to guide you through the day, not a medical measurement.
          </FAQ>
          <FAQ id="target" q="How is my daily target calculated?" openId={openId} setOpenId={setOpenId}>
            It starts from your body metrics, then adjusts for your lifestyle, your activity, and your climate. Connecting a wearable personalizes it further. You can change any of these inputs in My Health and your target updates immediately.
          </FAQ>
          <FAQ id="absorb" q="Why does intake not equal hydration?" openId={openId} setOpenId={setOpenId}>
            Generosity measures absorption, not just intake. Two glasses of water are not always hydrating to the same degree, so your score reflects how much your body is likely taking up, not only how much went in.
          </FAQ>
          <FAQ id="filter" q="What does Multi-Stage filtration do?" openId={openId} setOpenId={setOpenId}>
            Multi-Stage filtration in the Home WTR Hub Gen 2 reduces a broad range of common tap-water contaminants before the water reaches you. Your readings reflect water after filtration.
          </FAQ>
        </Section>

        <Section icon="ring" title="Connecting your devices">
          <FAQ id="oura" q="How do I connect my Oura Ring?" openId={openId} setOpenId={setOpenId}>
            Open Devices & Integrations. Sign in to your Oura account at cloud.ouraring.com/personal-access-tokens, create a personal access token, and paste it into the Oura field. We verify it pulls your data before saving. You can disconnect any time from the same screen.
          </FAQ>
          <FAQ id="bottle" q="How do I pair my Intelligent WTR Bottle?" openId={openId} setOpenId={setOpenId}>
            Power up the Smart Cap, then open the WTR Bottle screen and tap Connect. On iPhone or iPad you will need a Bluetooth-capable browser such as Bluefy, since Safari does not support the connection. Once paired, your bottle is remembered for one-tap reconnects.
          </FAQ>
          <FAQ id="hub" q="How do I link my Home WTR Hub?" openId={openId} setOpenId={setOpenId}>
            In Devices & Integrations, enter your Hub device ID and tap Link Hub. Telemetry is already collected; linking simply associates a hub with your account so its readings power your hydration intelligence.
          </FAQ>
        </Section>

        <Section icon="wrench" title="Troubleshooting">
          <FAQ id="ble" q="My bottle will not connect" openId={openId} setOpenId={setOpenId}>
            Confirm Bluetooth is on, the Smart Cap is powered, and you are using a Bluetooth-capable browser (Bluefy on iOS, Chrome on Android or desktop). If it still fails, power-cycle the cap and try again. A page reload always needs one fresh tap to reconnect.
          </FAQ>
          <FAQ id="ouraerr" q="My Oura token was rejected" openId={openId} setOpenId={setOpenId}>
            Tokens can expire or be revoked. Generate a fresh personal access token at cloud.ouraring.com/personal-access-tokens and paste the new one. If it still fails, confirm your Oura account has recent data to share.
          </FAQ>
          <FAQ id="calib" q="A bottle reading shows a dash or Calibrating" openId={openId} setOpenId={setOpenId}>
            Some sensors only read once there is water past the sensor line, and a few values calibrate over the first uses. A dash means no valid reading yet, not a problem. Fill the bottle and run a water test.
          </FAQ>
        </Section>

        <Section icon="shield" title="Privacy & data">
          <FAQ id="privacy" q="Who can see my data?" openId={openId} setOpenId={setOpenId}>
            Your profile, hydration, and any connected wearable data are tied to your account and used to power your hydration intelligence. We do not sell your personal data. For the full policy, see generositywater.com.
          </FAQ>
        </Section>

        <Section icon="mail" title="Still need help?">
          <div style={{padding:"12px 0 16px"}}>
            <div style={{fontSize:13,color:C.muted,lineHeight:1.6,marginBottom:12}}>
              Reach our team and we will get back to you. Include your account email and what you were doing when the issue happened.
            </div>
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Generosity%20App%20Support`} style={{display:"inline-flex",alignItems:"center",gap:8,textDecoration:"none",padding:"12px 20px",borderRadius:12,background:`linear-gradient(135deg,${C.blue},${C.blueDark})`,color:"#fff",fontSize:14,fontWeight:800}}>
              <HIcon name="mail" size={16} color="#fff"/> Email support
            </a>
          </div>
        </Section>

        <div style={{fontSize:11,color:C.muted,textAlign:"center",lineHeight:1.5,padding:"4px 8px"}}>
          Generosity{"\u2122"} provides hydration and water-quality estimates to guide your choices. It is not medical advice.
        </div>

      </div>
    </div>
  );
}
